import Prism from 'prismjs';
import { TFunction } from 'i18next';
import { CheerioAPI } from 'cheerio';
import { Namespace } from '../../../../translation/namespace';
import { decodeHtmlEntities } from '../../../StringUtils';
import { murmurhash } from '../../../../lib/js/hash';

// disable automatic highlighting of Prism as we do it manually
Prism.manual = true;

/**
 * Looking for all classes with "important-notice", set by user in tinymce.
 * Adding and invisible span inside -> hint for screenreaders
 */
export function resolveImportantNotice(t: TFunction) {
  return ($: CheerioAPI) => {
    $('span.important-notice').each((_i, element) => {
      $(element).prepend(`<span class="invisible">${t(`${Namespace.Wysiwyg}:importantNotice`)}: </span>`);
    });

    return $;
  };
}

/**
 * This function uses Prism to manually highlight code samples.
 * It is needed to guarantee equality of server and client side rendering.
 * Therefore, the html snippet is parsed for <pre> elements with class language-*.
 * The matches are then parsed using Prism. highlight function according to
 * language information in class, part behind the dash of the class. The string,
 * enriched with information how highlight the code, is then used to replace
 * non highlighted raw snippet in raw content.
 */
export function highlightPreformattedCode($: CheerioAPI) {
  $('pre[class^="language-"]').each((_i, element) => {
    const language = element.attribs.class.split('-')[1];
    $(element)
      // code tag
      .contents()
      // text content representing code example
      .contents()
      .replaceWith(Prism.highlight(decodeHtmlEntities($(element).text()), Prism.languages[language], language));
  });

  return $;
}

function wrap(el: HTMLTableElement, wrapper: HTMLElement) {
  el.parentNode?.insertBefore(wrapper, el);
  wrapper.appendChild(el);
}

// simple check if element is bigger then container
function isOverflown(element: HTMLTableElement, container: HTMLDivElement) {
  return element.offsetWidth > container.offsetWidth;
}

/**
 * Wrapping Tables in responsive-table section in order to:
 *  - make them scrollable if table > container
 *  - make them focusable if table > container
 *  - make them readable by screen readers with proper label
 * tinymce could not easily wrap a table inside a section,
 * so we do it purely on client side as we need to calculate offset anyway.
 *
 * @param container, e.g reference to wysiwyg element
 * @param t, function that returns translation for a given key
 */
export function wrapTablesInResponsiveDiv(container: HTMLDivElement | null, t: TFunction) {
  const responsiveTableClassName = 'responsive-tables';
  if (container) {
    container.querySelectorAll('table').forEach((table) => {
      if (isOverflown(table, container) && table.parentElement?.className !== responsiveTableClassName) {
        const responsiveSection = document.createElement('section');
        responsiveSection.setAttribute('class', responsiveTableClassName);
        responsiveSection.setAttribute('tabindex', '0');
        responsiveSection.setAttribute('aria-label', t(`${Namespace.Wysiwyg}:tableContent`));
        wrap(table, responsiveSection);
      }
    });
  }
}

export function addIdToAllHeadersForToc($: CheerioAPI) {
  $('h1, h2, h3, h4, h5, h6').each((_i, element) => {
    $(element).attr('id', `table-of-contents-heading-${murmurhash($(element).text() || `${_i}`)}-${_i}`);
  });

  return $;
}
